import { useState } from "react";
import { postAdsIngestTemplateize } from "@/lib/adsAssistantClientEngagement";

type Props = {
  canIngestPosts: boolean;
  hasTemplates: boolean;
  adCopy: string;
  creativeDescription: string;
  hintTemplateId: string;
  templateId: string;
  slotFillJsonText: string;
  setTemplateId: (v: string) => void;
  setSlotFillJsonText: (v: string) => void;
};

export function AdsAssistantIngestTemplateizeButton(props: Props) {
  const {
    canIngestPosts,
    hasTemplates,
    adCopy,
    creativeDescription,
    hintTemplateId,
    templateId,
    slotFillJsonText,
    setTemplateId,
    setSlotFillJsonText,
  } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  async function run() {
    setError(null);
    setHint(null);
    const copy = adCopy.trim();
    if (!copy) {
      setError("Add post text first.");
      return;
    }
    if ((templateId.trim() || slotFillJsonText.trim()) && !window.confirm("Replace the selected template and slot JSON with AI suggestions?")) {
      return;
    }
    setLoading(true);
    try {
      const r = await postAdsIngestTemplateize({
        ad_copy: copy,
        ...(creativeDescription.trim() ? { creative_description: creativeDescription.trim() } : {}),
        ...(hintTemplateId.trim() ? { hint_template_id: hintTemplateId.trim() } : {}),
      });
      setLoading(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const { template_id, slot_fills, notes } = r.data;
      if (template_id) setTemplateId(template_id);
      else setTemplateId("");
      const keys = Object.keys(slot_fills);
      if (keys.length) {
        setSlotFillJsonText(JSON.stringify(slot_fills, null, 2));
      } else if (!template_id) {
        setSlotFillJsonText("");
      }
      setHint(notes.trim() || null);
    } catch {
      setLoading(false);
      setError("Something went wrong. Try again.");
    }
  }

  if (!hasTemplates) return null;

  return (
    <div className="rounded-lg border border-violet-200/80 bg-violet-50/50 px-2 py-2 dark:border-violet-900/50 dark:bg-violet-950/25">
      <p className="text-[10px] text-violet-950 dark:text-violet-100/90">
        AI proposes a template and slot splits from your pasted copy. You confirm or edit before saving.
      </p>
      <button
        type="button"
        disabled={loading || !adCopy.trim() || !canIngestPosts}
        onClick={() => void run()}
        className="mt-2 rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-950 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-800 dark:bg-zinc-900 dark:text-violet-100 dark:hover:bg-zinc-800"
      >
        {loading ? "Working…" : "Suggest template & slots"}
      </button>
      {error ? <p className="mt-1.5 text-[11px] text-red-700 dark:text-red-300">{error}</p> : null}
      {hint ? <p className="mt-1.5 text-[11px] text-violet-900 dark:text-violet-200/90">{hint}</p> : null}
    </div>
  );
}
